/**
 * Timezone utilities for the scheduling module.
 * Provides functions for formatting dates in specific timezones,
 * getting timezone information, and a curated list of common timezones.
 */

import { logger } from '@/lib/logger';

// ============================================================================
// Constants
// ============================================================================

/**
 * Reference dates for consistent offset calculations.
 * Uses both January (northern winter/southern summer) and July (northern summer/southern winter)
 * to derive standard time offset regardless of hemisphere.
 */
const WINTER_REFERENCE_DATE = new Date('2024-01-01T00:00:00Z');
const SUMMER_REFERENCE_DATE = new Date('2024-07-01T00:00:00Z');

// ============================================================================
// Types
// ============================================================================

export interface TimezoneOption {
  value: string;
  label: string;
  offset: string;
}

// ============================================================================
// Core Functions
// ============================================================================

/**
 * Format a date in a specific timezone with the given format options.
 *
 * @param date - The date to format (Date object, ISO string, or timestamp)
 * @param timezone - IANA timezone string (e.g., 'America/New_York')
 * @param options - Intl.DateTimeFormat options for formatting
 * @returns Formatted date string in the specified timezone
 *
 * @example
 * formatInTimezone(new Date(), 'America/New_York', { hour: 'numeric', minute: '2-digit' })
 * // => "3:30 PM"
 */
export function formatInTimezone(
  date: Date | string | number,
  timezone: string,
  options: Intl.DateTimeFormatOptions = {}
): string {
  const dateObj = date instanceof Date ? date : new Date(date);

  return new Intl.DateTimeFormat('en-US', {
    ...options,
    timeZone: timezone,
  }).format(dateObj);
}

/**
 * Get the BROWSER/system timezone — NOT the user's profile timezone.
 *
 * ⚠️ Rendering any date in this timezone is forbidden on authenticated
 * surfaces: dates always render in the user's profile timezone (which the
 * backend resolves to a non-empty value — `profileData.user.timezone`). The
 * only sanctioned uses of the browser timezone are (a) the public booking
 * pages, where the anonymous visitor has no profile, (b) suggesting a
 * default in a timezone *picker* the user explicitly confirms (onboarding),
 * and (c) the calendar page (`pages/calendar`), which is tracked migration
 * debt — it still renders browser-local end to end, and pins this timezone
 * explicitly (see WeekDayColumn) until the page-wide profile-timezone
 * migration lands.
 *
 * @returns IANA timezone string (e.g., 'America/Los_Angeles')
 */
export function getBrowserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Get the abbreviated timezone name (e.g., PST, EST, GMT).
 *
 * @param timezone - IANA timezone string
 * @param date - Optional date to use for DST-aware abbreviation (defaults to now)
 * @returns Short timezone abbreviation
 *
 * @example
 * getTimezoneAbbreviation('America/New_York')
 * // => "EST" (or "EDT" during daylight saving)
 */
export function getTimezoneAbbreviation(
  timezone: string,
  date: Date = new Date()
): string {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'short',
  });

  const parts = formatter.formatToParts(date);
  const timeZonePart = parts.find(part => part.type === 'timeZoneName');

  return timeZonePart?.value || timezone;
}

/**
 * Get the UTC offset string for a timezone (e.g., "UTC-5", "UTC+5:30").
 *
 * @param timezone - IANA timezone string
 * @param date - Optional date to use for DST-aware offset (defaults to now)
 * @returns UTC offset string
 *
 * @example
 * getTimezoneOffset('America/New_York')
 * // => "UTC-5" (or "UTC-4" during daylight saving)
 */
export function getTimezoneOffset(
  timezone: string,
  date: Date = new Date()
): string {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'longOffset',
  });

  const parts = formatter.formatToParts(date);
  const timeZonePart = parts.find(part => part.type === 'timeZoneName');

  // The longOffset format gives us something like "GMT-05:00"
  // We want to return "UTC-5" format
  const offset = timeZonePart?.value || 'UTC';

  // Convert "GMT-05:00" to "UTC-5" format
  const match = offset.match(/^GMT([+-])(\d{2}):(\d{2})$/);
  if (match) {
    const [, sign, hours, minutes] = match;
    const hourNum = parseInt(hours, 10);
    const minNum = parseInt(minutes, 10);

    if (minNum === 0) {
      return hourNum === 0 ? 'UTC' : `UTC${sign}${hourNum}`;
    }
    return `UTC${sign}${hourNum}:${minutes}`;
  }

  // Fallback for edge cases
  return offset.replace('GMT', 'UTC');
}

/**
 * Format a date with time and timezone indicator.
 *
 * @param date - The date to format
 * @param timezone - IANA timezone string
 * @returns Formatted string like "3:30 PM PST"
 */
export function formatTimeWithTimezone(
  date: Date | string | number,
  timezone: string
): string {
  const dateObj = date instanceof Date ? date : new Date(date);

  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
    timeZoneName: 'short',
  }).format(dateObj);
}

/**
 * Get the hour (0-23) of a date in a specific timezone.
 *
 * @param date - The date to extract the hour from (Date object, ISO string, or timestamp)
 * @param timezone - IANA timezone string (e.g., 'America/New_York')
 * @returns Hour in the specified timezone (0-23)
 *
 * @example
 * getHourInTimezone('2025-01-15T17:00:00Z', 'America/New_York')
 * // => 12
 */
export function getHourInTimezone(
  date: Date | string | number,
  timezone: string
): number {
  const dateObj = date instanceof Date ? date : new Date(date);

  const parts = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    hour12: false,
    timeZone: timezone,
  }).formatToParts(dateObj);

  const hourPart = parts.find(part => part.type === 'hour');
  const hour = parseInt(hourPart?.value || '0', 10);

  // Intl can return 24 for midnight in hour12:false mode
  return hour === 24 ? 0 : hour;
}

/**
 * Get the minute (0-59) of a date in a specific timezone.
 *
 * @param date - The date to extract the minute from (Date object, ISO string, or timestamp)
 * @param timezone - IANA timezone string (e.g., 'America/New_York')
 * @returns Minute in the specified timezone (0-59)
 *
 * @example
 * getMinuteInTimezone('2025-01-15T17:30:00Z', 'America/New_York')
 * // => 30
 */
export function getMinuteInTimezone(
  date: Date | string | number,
  timezone: string
): number {
  const dateObj = date instanceof Date ? date : new Date(date);

  const parts = new Intl.DateTimeFormat('en-US', {
    minute: 'numeric',
    timeZone: timezone,
  }).formatToParts(dateObj);

  const minutePart = parts.find(part => part.type === 'minute');
  return parseInt(minutePart?.value || '0', 10);
}

/**
 * Get the year, month, and day of a date in a specific timezone.
 *
 * @param date - The date to extract parts from (Date object, ISO string, or timestamp)
 * @param timezone - IANA timezone string (e.g., 'America/New_York')
 * @returns Object with year, month (1-12), and day (1-31)
 *
 * @example
 * getDatePartsInTimezone('2025-01-16T03:00:00Z', 'America/New_York')
 * // => { year: 2025, month: 1, day: 15 }
 */
export function getDatePartsInTimezone(
  date: Date | string | number,
  timezone: string
): { year: number; month: number; day: number } {
  const dateObj = date instanceof Date ? date : new Date(date);

  const parts = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    timeZone: timezone,
  }).formatToParts(dateObj);

  const year = parseInt(
    parts.find(part => part.type === 'year')?.value || '0',
    10
  );
  const month = parseInt(
    parts.find(part => part.type === 'month')?.value || '0',
    10
  );
  const day = parseInt(
    parts.find(part => part.type === 'day')?.value || '0',
    10
  );

  return { year, month, day };
}

/**
 * Create a Date object from wall-clock components in a specific timezone.
 * Handles DST transitions correctly by using the offset that applies at the target time.
 *
 * @param year - Year (e.g., 2025)
 * @param month - Month (1-12, not 0-11)
 * @param day - Day of month (1-31)
 * @param hour - Hour (0-23)
 * @param minute - Minute (0-59)
 * @param second - Second (0-59, defaults to 0)
 * @param timezone - IANA timezone string (e.g., 'America/New_York')
 * @returns Date object representing that wall-clock time in the timezone
 *
 * @example
 * dateFromComponentsInTimezone(2025, 3, 9, 14, 30, 0, 'America/New_York')
 * // => Date object for 2025-03-09T14:30:00 in New York time
 */
export function dateFromComponentsInTimezone(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number = 0,
  timezone: string
): Date {
  // Strategy: Build an ISO string with wall-clock components and a guessed offset,
  // then parse and re-format to get the correct offset.

  // First, make an initial guess using a reference date in the target month
  // This helps get the right offset for most cases (DST-aware)
  const referenceDate = new Date(Date.UTC(year, month - 1, 15, 12, 0, 0));
  const guessedOffsetParts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'longOffset',
  }).formatToParts(referenceDate);
  const guessedOffsetStr =
    guessedOffsetParts.find(p => p.type === 'timeZoneName')?.value || 'GMT';
  const guessedOffsetMatch = guessedOffsetStr.match(/^GMT([+-]\d{2}:\d{2})$/);
  const guessedOffset = guessedOffsetMatch ? guessedOffsetMatch[1] : '+00:00';

  // Build ISO string with wall-clock components and guessed offset
  const yearStr = year.toString().padStart(4, '0');
  const monthStr = month.toString().padStart(2, '0');
  const dayStr = day.toString().padStart(2, '0');
  const hourStr = hour.toString().padStart(2, '0');
  const minuteStr = minute.toString().padStart(2, '0');
  const secondStr = second.toString().padStart(2, '0');
  const isoStr = `${yearStr}-${monthStr}-${dayStr}T${hourStr}:${minuteStr}:${secondStr}${guessedOffset}`;

  // Parse to get initial Date object
  let date = new Date(isoStr);

  // Verify the wall-clock time matches what we want in the target timezone
  // If not (due to DST transition or wrong guess), iterate to find the correct UTC instant
  const actualParts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: timezone,
  }).formatToParts(date);

  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    actualParts.find(p => p.type === type)?.value || '00';

  let actualHour = get('hour');
  if (actualHour === '24') {
    actualHour = '00';
  }

  const actualYear = parseInt(get('year'), 10);
  const actualMonth = parseInt(get('month'), 10);
  const actualDay = parseInt(get('day'), 10);
  const actualHourNum = parseInt(actualHour, 10);
  const actualMinute = parseInt(get('minute'), 10);
  const actualSecond = parseInt(get('second'), 10);

  // If wall-clock time doesn't match, we need to adjust
  // This can happen during DST "spring forward" gaps or "fall back" ambiguity
  if (
    actualYear !== year ||
    actualMonth !== month ||
    actualDay !== day ||
    actualHourNum !== hour ||
    actualMinute !== minute ||
    actualSecond !== second
  ) {
    // Calculate the difference and adjust (using full wall-clock difference)
    const targetWallTime = Date.UTC(year, month - 1, day, hour, minute, second);
    const actualWallTime = Date.UTC(
      actualYear,
      actualMonth - 1,
      actualDay,
      actualHourNum,
      actualMinute,
      actualSecond
    );

    // Adjust by full wall-clock difference (includes date rollover)
    date = new Date(date.getTime() + (targetWallTime - actualWallTime));
  }

  return date;
}

/**
 * Format a Date as an ISO-8601 string with the timezone's UTC offset.
 * Uses the actual offset at the given instant (DST-aware).
 *
 * @param date - The date to format
 * @param timezone - IANA timezone string (e.g., 'America/New_York')
 * @returns ISO-8601 string like "2025-01-15T10:00:00-05:00"
 *
 * @example
 * formatISOInTimezone(new Date('2025-01-15T15:00:00Z'), 'America/New_York')
 * // => "2025-01-15T10:00:00-05:00"
 */
export function formatISOInTimezone(date: Date, timezone: string): string {
  // Get date and time parts using en-CA locale for YYYY-MM-DD ordering
  const dateParts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: timezone,
  }).formatToParts(date);

  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    dateParts.find(p => p.type === type)?.value || '00';

  let hour = get('hour');
  // Intl can return "24" for midnight in hour12:false mode
  if (hour === '24') {
    hour = '00';
  }

  const dateStr = `${get('year')}-${get('month')}-${get('day')}`;
  const timeStr = `${hour}:${get('minute')}:${get('second')}`;

  // Get the UTC offset using longOffset format (e.g., "GMT-05:00")
  const offsetParts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'longOffset',
  }).formatToParts(date);

  const offsetStr =
    offsetParts.find(p => p.type === 'timeZoneName')?.value || 'GMT';

  // Convert "GMT-05:00" -> "-05:00", "GMT+05:30" -> "+05:30", "GMT" -> "+00:00"
  let offset: string;
  const offsetMatch = offsetStr.match(/^GMT([+-]\d{2}:\d{2})$/);
  if (offsetMatch) {
    offset = offsetMatch[1];
  } else {
    offset = '+00:00';
  }

  return `${dateStr}T${timeStr}${offset}`;
}

// ============================================================================
// Common Timezones
// ============================================================================

/**
 * Curated list of common timezones for timezone selector dropdowns.
 * Organized by region with human-readable labels.
 */
export const COMMON_TIMEZONES: TimezoneOption[] = [
  // UTC
  { value: 'UTC', label: 'UTC', offset: 'UTC' },

  // North America
  {
    value: 'America/New_York',
    label: 'Eastern Time (US & Canada)',
    offset: 'UTC-5',
  },
  {
    value: 'America/Chicago',
    label: 'Central Time (US & Canada)',
    offset: 'UTC-6',
  },
  {
    value: 'America/Denver',
    label: 'Mountain Time (US & Canada)',
    offset: 'UTC-7',
  },
  {
    value: 'America/Los_Angeles',
    label: 'Pacific Time (US & Canada)',
    offset: 'UTC-8',
  },
  {
    value: 'America/Anchorage',
    label: 'Alaska',
    offset: 'UTC-9',
  },
  {
    value: 'Pacific/Honolulu',
    label: 'Hawaii',
    offset: 'UTC-10',
  },
  {
    value: 'America/Phoenix',
    label: 'Arizona',
    offset: 'UTC-7',
  },
  {
    value: 'America/Toronto',
    label: 'Toronto',
    offset: 'UTC-5',
  },
  {
    value: 'America/Vancouver',
    label: 'Vancouver',
    offset: 'UTC-8',
  },

  // Central/South America
  {
    value: 'America/Mexico_City',
    label: 'Mexico City',
    offset: 'UTC-6',
  },
  {
    value: 'America/Sao_Paulo',
    label: 'Sao Paulo',
    offset: 'UTC-3',
  },
  {
    value: 'America/Argentina/Buenos_Aires',
    label: 'Buenos Aires',
    offset: 'UTC-3',
  },
  {
    value: 'America/Santiago',
    label: 'Santiago',
    offset: 'UTC-4',
  },
  {
    value: 'America/Bogota',
    label: 'Bogota',
    offset: 'UTC-5',
  },
  {
    value: 'America/Lima',
    label: 'Lima',
    offset: 'UTC-5',
  },

  // Europe
  {
    value: 'Europe/London',
    label: 'London',
    offset: 'UTC+0',
  },
  {
    value: 'Europe/Paris',
    label: 'Paris',
    offset: 'UTC+1',
  },
  {
    value: 'Europe/Berlin',
    label: 'Berlin',
    offset: 'UTC+1',
  },
  {
    value: 'Europe/Amsterdam',
    label: 'Amsterdam',
    offset: 'UTC+1',
  },
  {
    value: 'Europe/Madrid',
    label: 'Madrid',
    offset: 'UTC+1',
  },
  {
    value: 'Europe/Rome',
    label: 'Rome',
    offset: 'UTC+1',
  },
  {
    value: 'Europe/Zurich',
    label: 'Zurich',
    offset: 'UTC+1',
  },
  {
    value: 'Europe/Stockholm',
    label: 'Stockholm',
    offset: 'UTC+1',
  },
  {
    value: 'Europe/Dublin',
    label: 'Dublin',
    offset: 'UTC+0',
  },
  {
    value: 'Europe/Helsinki',
    label: 'Helsinki',
    offset: 'UTC+2',
  },
  {
    value: 'Europe/Moscow',
    label: 'Moscow',
    offset: 'UTC+3',
  },
  {
    value: 'Europe/Istanbul',
    label: 'Istanbul',
    offset: 'UTC+3',
  },

  // Asia
  {
    value: 'Asia/Dubai',
    label: 'Dubai',
    offset: 'UTC+4',
  },
  {
    value: 'Asia/Kabul',
    label: 'Kabul',
    offset: 'UTC+4:30',
  },
  {
    value: 'Asia/Kolkata',
    label: 'India Standard Time',
    offset: 'UTC+5:30',
  },
  {
    value: 'Asia/Colombo',
    label: 'Colombo',
    offset: 'UTC+5:30',
  },
  {
    value: 'Asia/Kathmandu',
    label: 'Kathmandu',
    offset: 'UTC+5:45',
  },
  {
    value: 'Asia/Bangkok',
    label: 'Bangkok',
    offset: 'UTC+7',
  },
  {
    value: 'Asia/Singapore',
    label: 'Singapore',
    offset: 'UTC+8',
  },
  {
    value: 'Asia/Hong_Kong',
    label: 'Hong Kong',
    offset: 'UTC+8',
  },
  {
    value: 'Asia/Shanghai',
    label: 'Beijing, Shanghai',
    offset: 'UTC+8',
  },
  {
    value: 'Asia/Tokyo',
    label: 'Tokyo',
    offset: 'UTC+9',
  },
  {
    value: 'Asia/Seoul',
    label: 'Seoul',
    offset: 'UTC+9',
  },
  {
    value: 'Asia/Manila',
    label: 'Manila',
    offset: 'UTC+8',
  },
  {
    value: 'Asia/Jakarta',
    label: 'Jakarta',
    offset: 'UTC+7',
  },
  {
    value: 'Asia/Jerusalem',
    label: 'Jerusalem',
    offset: 'UTC+2',
  },

  // Australia & Pacific
  {
    value: 'Australia/Sydney',
    label: 'Sydney',
    offset: 'UTC+10',
  },
  {
    value: 'Australia/Melbourne',
    label: 'Melbourne',
    offset: 'UTC+10',
  },
  {
    value: 'Australia/Brisbane',
    label: 'Brisbane',
    offset: 'UTC+10',
  },
  {
    value: 'Australia/Perth',
    label: 'Perth',
    offset: 'UTC+8',
  },
  {
    value: 'Australia/Adelaide',
    label: 'Adelaide',
    offset: 'UTC+9:30',
  },
  {
    value: 'Pacific/Auckland',
    label: 'Auckland',
    offset: 'UTC+12',
  },
  {
    value: 'Pacific/Fiji',
    label: 'Fiji',
    offset: 'UTC+12',
  },

  // Africa
  {
    value: 'Africa/Cairo',
    label: 'Cairo',
    offset: 'UTC+2',
  },
  {
    value: 'Africa/Johannesburg',
    label: 'Johannesburg',
    offset: 'UTC+2',
  },
  {
    value: 'Africa/Lagos',
    label: 'Lagos',
    offset: 'UTC+1',
  },
  {
    value: 'Africa/Nairobi',
    label: 'Nairobi',
    offset: 'UTC+3',
  },
];

/**
 * Find a timezone option by its value.
 *
 * @param timezone - IANA timezone string to find
 * @returns TimezoneOption if found, undefined otherwise
 */
export function findTimezoneOption(
  timezone: string
): TimezoneOption | undefined {
  return COMMON_TIMEZONES.find(tz => tz.value === timezone);
}

/**
 * Get a display label for a timezone.
 * Falls back to the timezone value if not found in common list.
 *
 * @param timezone - IANA timezone string
 * @returns Human-readable timezone label
 */
export function getTimezoneLabel(timezone: string): string {
  const option = findTimezoneOption(timezone);
  if (option) {
    return option.label;
  }
  // Format IANA timezone to readable format (e.g., "America/New_York" -> "America/New York")
  return timezone.replace(/_/g, ' ');
}

/**
 * Normalize a detected timezone to a target timezone list.
 * 1. If exact match exists, return it
 * 2. Otherwise, find a timezone with matching UTC offset using REFERENCE_DATE
 * 3. Fall back to UTC if no match
 *
 * @param detected - Browser-detected IANA timezone
 * @param targetList - List of timezone options to normalize to
 * @returns Normalized timezone from targetList or 'UTC'
 */
export function normalizeToTimezoneList(
  detected: string,
  targetList: readonly { value: string }[]
): string {
  // 1. Exact match check
  if (targetList.some(tz => tz.value === detected)) {
    return detected;
  }

  // 2. UTC offset matching - get standard time offset by comparing winter and summer
  // Standard offset is the larger absolute value (further from UTC) to avoid DST
  let detectedStandardOffset: string;
  try {
    const winterParts = new Intl.DateTimeFormat('en-US', {
      timeZone: detected,
      timeZoneName: 'longOffset',
    }).formatToParts(WINTER_REFERENCE_DATE);
    const summerParts = new Intl.DateTimeFormat('en-US', {
      timeZone: detected,
      timeZoneName: 'longOffset',
    }).formatToParts(SUMMER_REFERENCE_DATE);

    const winterOffset =
      winterParts.find(p => p.type === 'timeZoneName')?.value || '';
    const summerOffset =
      summerParts.find(p => p.type === 'timeZoneName')?.value || '';

    // Standard time offset is the one further from UTC (larger absolute offset)
    // For northern hemisphere: winter offset is standard (e.g., GMT-5 vs GMT-4)
    // For southern hemisphere: summer offset is standard (e.g., GMT+10 vs GMT+11)
    detectedStandardOffset = getStandardOffset(winterOffset, summerOffset);
  } catch {
    return 'UTC';
  }

  const match = targetList.find(tz => {
    if (!tz.value) {
      return false;
    }
    // Skip UTC to avoid incorrectly matching non-UTC timezones with +00:00 offset
    if (tz.value === 'UTC') {
      return false;
    }
    try {
      const winterParts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz.value,
        timeZoneName: 'longOffset',
      }).formatToParts(WINTER_REFERENCE_DATE);
      const summerParts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz.value,
        timeZoneName: 'longOffset',
      }).formatToParts(SUMMER_REFERENCE_DATE);

      const winterOffset =
        winterParts.find(p => p.type === 'timeZoneName')?.value || '';
      const summerOffset =
        summerParts.find(p => p.type === 'timeZoneName')?.value || '';

      const standardOffset = getStandardOffset(winterOffset, summerOffset);
      return standardOffset === detectedStandardOffset;
    } catch {
      return false;
    }
  });

  return match?.value || 'UTC';
}

/**
 * Get the standard time offset from winter and summer offsets.
 * Standard offset is the one with the smaller numeric value (DST adds +1 hour).
 */
function getStandardOffset(winterOffset: string, summerOffset: string): string {
  // Parse offset to numeric value (e.g., "GMT-5:00" -> -5, "GMT+1:00" -> 1)
  const parseOffset = (offset: string): number => {
    const match = offset.match(/^GMT([+-])(\d{2}):(\d{2})$/);
    if (!match) {
      return 0;
    }
    const [, sign, hours, minutes] = match;
    const value = parseInt(hours, 10) + parseInt(minutes, 10) / 60;
    return sign === '-' ? -value : value;
  };

  const winterValue = parseOffset(winterOffset);
  const summerValue = parseOffset(summerOffset);

  // Return the offset with the smaller numeric value (DST increases offsets by +1 hour)
  return winterValue <= summerValue ? winterOffset : summerOffset;
}

/**
 * Map a detected timezone to the nearest common timezone.
 * 1. If exact match exists in COMMON_TIMEZONES, return it
 * 2. Otherwise, find a timezone with matching UTC offset
 * 3. Fall back to UTC if no match
 */
export function mapToCommonTimezone(detected: string): string {
  const result = normalizeToTimezoneList(detected, COMMON_TIMEZONES);

  // Log if we had to fall back (for debugging)
  if (result === 'UTC' && detected !== 'UTC') {
    logger.warn('Timezone normalized to UTC', {
      component: 'timezone',
      detected,
      referenceDates: {
        winter: WINTER_REFERENCE_DATE.toISOString(),
        summer: SUMMER_REFERENCE_DATE.toISOString(),
      },
    });
  }

  return result;
}
